import React, { ChangeEvent, forwardRef, useState, MouseEvent, Fragment, useEffect } from 'react';
import './FileUpload.scss';
import {
  formatExtensions,
  getFilesSize,
  isFileExists,
  isValidExtension,
  loadFileTypeIcon,
} from './FileUpload.helpers';

export interface FileUploadData {
  FileSize: number;
  FileExtensions: string;
  FileSizeValidationMessage: string;
  FileUploadButtonLabel: string;
  ValidationMessage?: string;
}

export interface FileUploadProps {
  name: string;
  label: string;
  placeholder?: string;
  required: boolean;
  btnLabel: string;
  extensions: string;
  maxSize: number;
  validationErrorMsg: string;
  sizeValidationMsg: string;
  onFilesUpdate: (files: File[]) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const FileUpload = forwardRef<HTMLInputElement, FileUploadProps>(
  (
    {
      name = 'ImageUpload',
      label,
      placeholder,
      required,
      extensions,
      btnLabel,
      maxSize,
      onFilesUpdate,
      validationErrorMsg,
      sizeValidationMsg,
      onChange,
      ...props
    },
    ref
  ) => {
    const [files, setFiles] = useState<File[]>([]);
    const [errors, setErrors] = useState({
      fileExistsError: false,
      validFileError: false,
      isMaxFileSizeError: false,
    });

    useEffect(() => {
      //submit to the form
      onFilesUpdate(files);
    }, [files]);

    const getIsValid = (err: typeof errors) => {
      return Object.values(err).every((e) => e === false);
    };

    const handleUpload = (e: ChangeEvent<HTMLInputElement>) => {
      const newFiles = Array.from(e.target.files);
      const allFiles = [...newFiles, ...files];
      const errors = {
        fileExistsError: isFileExists(files, newFiles),
        validFileError: !isValidExtension(newFiles, extensions.split(',')),
        isMaxFileSizeError: !!(maxSize && getFilesSize(allFiles) > maxSize),
      };

      const isValid = getIsValid(errors);
      setErrors(errors);

      if (isValid) {
        setFiles(allFiles);
      }
    };

    const onClick = (e: MouseEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      target.value = '';
    };

    const removeFile = (arrIndex: number) => {
      const updatedArr = files.filter((_, index) => index !== arrIndex);
      setFiles(updatedArr);
    };

    return (
      <div className='c-common-input'>
        <div className='c-contact-upload-row'>
          <div className='c-contact-upload-ico'>
            <div className='upload-btn-wrapper'>
              <button className='btn'>
                <i>
                  <div className='icon-file-upload' />
                </i>
                <span>{btnLabel || 'Upload files'}</span>
              </button>
              <input
                ref={ref}
                onChange={handleUpload}
                onClick={onClick}
                type='file'
                name={name}
                multiple
                placeholder={placeholder}
                accept={formatExtensions(extensions.split(','))}
                {...props}
              />
            </div>
          </div>
          <div className='c-file-upload-label'>
            {label && (
              <label htmlFor={name}>
                {label}
                {required && ' *'}
              </label>
            )}
          </div>
        </div>

        <div>
          {errors.fileExistsError && (
            <>
              <span className='c-common-validation c-common-validation-danger'>
                File already exists
              </span>
              <br />
            </>
          )}
          {errors.validFileError && (
            <>
              <span className='c-common-validation c-common-validation-danger'>
                {validationErrorMsg
                  ? validationErrorMsg.replace('{0}', extensions)
                  : 'Error while uploading the file'}
              </span>
              <br />
            </>
          )}
          {errors.isMaxFileSizeError && (
            <>
              <span className='c-common-validation c-common-validation-danger'>
                {sizeValidationMsg
                  ? sizeValidationMsg.replace('{1}', maxSize.toString())
                  : 'File size error'}
              </span>
              <br />
            </>
          )}
        </div>
        {files.length > 0 && (
          <div className='c-common-validation c-upload-files'>
            {files.map(({ name }, index) => {
              const iconClass = loadFileTypeIcon(name);
              return (
                <Fragment key={index.toString()}>
                  <label>
                    <div className='c-upload-flexdir-left'>
                      <span className='c-file-icon'>
                        <i className={`${iconClass}`}></i>
                      </span>
                      <span className='c-upload-file-label'>{name}</span>
                    </div>
                    <div className='c-upload-flexdir-right'>
                      <span className='c-icon-close'>
                        <i
                          className='icon-cancel-circle'
                          onClick={() => {
                            removeFile(index);
                          }}
                        ></i>
                      </span>
                    </div>
                  </label>
                  <hr />
                </Fragment>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);
